import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { warehouseAPI } from '../api/warehouseAPI'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import InventoryItemCard from '../components/InventoryItemCard'
import { usePermissions } from '../contexts/PermissionContext'
import 'material-icons/iconfont/material-icons.css';
import Select from 'react-select'
import '../styles/selectControl.css'

function InventoryTransferScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { inventoryItemId } = useParams()
    const [searchParams] = useSearchParams()
    const [error, setError] = useState(null)
    const [warehouses, setWarehouses] = useState([])
    const [inventoryItem, setInventoryItem] = useState({
        stock_item: null,
        warehouse: { _id: '' },
        location: '',
        new_warehouse: '',
        quantity: '',
        comments: '',
        max_quantity: 0,
    })

    useEffect(() => {
        async function fetchInventoryItem() {
            try {
                const item = await inventoryItemAPI.getById(inventoryItemId)
                
                if (!item) return;
                    
                const matchingWarehouse = warehouses.find(w => w.name === item.warehouse_name)
                if (!matchingWarehouse) {
                    return
                }
                if (matchingWarehouse) {
                    const warehouses = await warehouseAPI.getAll()
                    // reduce the current warehouse from the list of warehouses
                    const currentWarehouse = warehouses.find(w => w.name === item.warehouse_name)
                    const filteredWarehouses = warehouses.filter(w => w._id !== currentWarehouse._id)
                    setWarehouses(filteredWarehouses)
                }
                    
                const formattedItem = {
                    stock_item: item.stock_item,
                    warehouse_name: item.warehouse_name,
                    location: item.location_name,
                    new_location_id: '',
                    quantity: '',
                    vendor: item.vendor_name,
                    manufacturer: item.manufacturer_name,
                    cost: item.cost,
                    notes: item.notes,
                    bb_date: item.bb_date,
                    lot: item.lot,
                    comments: item.comments || '',
                    max_quantity: item.quantity || 0,
                }
                
                setInventoryItem(formattedItem)
            } catch (error) {
                setError('Failed to load inventory item')
            }
        }

        if (inventoryItemId && warehouses.length > 0) {
            fetchInventoryItem()
        }
    }, [inventoryItemId, warehouses])

    useEffect(() => {
        async function fetchWarehouses() {
            try {
                const response = await warehouseAPI.getAll()
                setWarehouses(response)
            } catch (error) {
                setError('Failed to load warehouses')
            }
        }
        fetchWarehouses()
    }, [])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await inventoryItemAPI.transfer(inventoryItemId, inventoryItem.quantity, inventoryItem.new_warehouse, inventoryItem.comments)
            
            // Get the return filters from the location state if they exist
            // Check for returnTo query parameter first, then fall back to location state filters
            const returnTo = searchParams.get('returnTo')
            if (returnTo) {
                navigate(returnTo)
            } else {
                const returnFilters = location.state?.returnFilters || ''
                
                // Add refresh parameter to trigger data refresh
                const separator = returnFilters.includes('?') ? '&' : '?'
                const refreshParam = `${separator}refresh=1`
                
                // Navigate back to inventory with the filters preserved and refresh trigger
                navigate(`/inventory${returnFilters}${refreshParam}`)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    // Create warehouse options for the select dropdown
    const warehouseOptions = warehouses.map(warehouse => ({
        value: warehouse._id,
        label: warehouse.name
    }))

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('inventory.move')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to transfer inventory items.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
            <Col style={{ maxWidth: '730px' }}>
            <h1>Transfer Inventory Item {inventoryItemId}</h1>
            {inventoryItem?.stock_item && (
                <InventoryItemCard inventoryItem={inventoryItem} />
            )}
            <div style={{ height: '20px' }}></div>
            <Form onSubmit={submitHandler}>
                <table className="table table-borderless table-striped" style={{ marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '200px' }}>
                                <Form.Group className="mb-4" controlId="quantity">
                                    <Form.Label>Quantity* (max {inventoryItem.max_quantity}):</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max={inventoryItem.max_quantity}
                                        step="1"
                                        value={inventoryItem.quantity}
                                        onChange={(e) => setInventoryItem({...inventoryItem, quantity: e.target.value})}
                                        required
                                        placeholder="Enter quantity"
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                </Form.Group>
                            </td>
                    
                            <td style={{ 
                                textAlign: 'center', 
                                verticalAlign: 'bottom',
                                paddingBottom: '2rem',
                                paddingRight: '0'
                            }}>
                                <i className="material-icons" style={{ fontSize: '2.5rem' }}>local_shipping</i> 
                            </td>
                            <td style={{ 
                                textAlign: 'left', 
                                verticalAlign: 'bottom',
                                paddingBottom: '2rem',
                                paddingLeft: '0'
                            }}>
                                <i className="material-icons" style={{ fontSize: '1.5rem' }}>chevron_right</i> 
                            </td>
                            <td style={{ width: '250px' }}>
                                <Form.Group className="mb-4" controlId="warehouse" >
                                    <Form.Label>To Warehouse*:</Form.Label>
                                    <Select
                                        value={warehouseOptions.find(option => option.value === inventoryItem.new_warehouse)}
                                        onChange={(selectedOption) => setInventoryItem({
                                            ...inventoryItem, 
                                            new_warehouse: selectedOption ? selectedOption.value : ''
                                        })}
                                        options={warehouseOptions}
                                        placeholder="Select Warehouse"
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        required
                                    />
                                </Form.Group>
                            </td>
                        </tr>
                    </tbody>
                </table>
                
                <Form.Group className="mb-4" controlId="notes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        value={inventoryItem.comments}
                        onChange={(e) => setInventoryItem({...inventoryItem, comments: e.target.value})}
                        placeholder="Enter notes (max 500 characters)"
                        autoComplete="off"
                        spellCheck="true"
                    />
                    <Form.Text className="text-muted">
                        {inventoryItem.comments?.length || 0}/500 characters
                    </Form.Text>
                </Form.Group>
                {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
                )}
                <Button variant="success" type="submit" className="me-2">
                    Start Transfer
                </Button>

                <Button 
                    variant="secondary" 
                    onClick={() => navigate(-1)}
                >
                    Cancel
                </Button>
            </Form>
            
            </Col>
            </Row>
        </Container>
    )
}

export default InventoryTransferScreen 